// v1.7.0 · CRM · Contactos: búsqueda, filtros, listas guardadas, acciones masivas, importar/exportar y alta
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Upload, Download, Filter, Search, Tag as TagIcon, UserCheck, Trash2, Bookmark, X } from 'lucide-react';
import { api, errMsg, fdate, fdatetime, Tag, Modal, inputCls, btn, Field, OwnOwner, CustomInput, Empty, downloadBlob } from './ui';
import { LeadBadge } from './Assistant';   // v1.14.0 · calificación del asistente

const EMPTY_FILTERS = { q: '', tags: '', source: '', owner_id: '', created_from: '', created_to: '', has_email: '', has_phone: '', sort: 'created_desc' };
const SORTS = [['created_desc', 'Más recientes'], ['created_asc', 'Más antiguos'], ['activity_desc', 'Última actividad'], ['name_asc', 'Nombre A-Z'], ['name_desc', 'Nombre Z-A']];

const toParams = (f) => {
  const p = {};
  Object.entries(f).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) p[k] = v; });
  return p;
};

export function ContactForm({ meta, onClose, onSaved }) {
  const [c, setC] = useState({ first_name: '', last_name: '', email: '', phone: '', company: '', source: '', city: '', country: '', tags: [], owner_id: '', custom: {} });
  const [tagInput, setTagInput] = useState('');
  const [dup, setDup] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !c.tags.includes(t)) setC({ ...c, tags: [...c.tags, t] });
    setTagInput('');
  };
  const save = async () => {
    setSaving(true); setDup(null);
    try {
      const r = await api.post('/contacts', { ...c, owner_id: c.owner_id || null });
      toast.success('Contacto creado');
      onSaved(r);
    } catch (e) {
      if (e?.response?.status === 409) setDup(e.response.data.detail);
      else toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Nuevo contacto" onClose={onClose} wide
           footer={<><button className={btn.ghost} onClick={onClose}>Cancelar</button><button className={btn.primary} onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Crear contacto'}</button></>}>
      {dup && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {dup.message} <button className="font-semibold underline" onClick={() => navigate(`/crm/contactos/${dup.contact_id}`)}>Abrir el existente</button>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nombre"><input className={inputCls} value={c.first_name} onChange={set('first_name')} autoFocus /></Field>
        <Field label="Apellido"><input className={inputCls} value={c.last_name} onChange={set('last_name')} /></Field>
        <Field label="Correo"><input className={inputCls} type="email" value={c.email} onChange={set('email')} /></Field>
        <Field label="Teléfono"><input className={inputCls} type="tel" value={c.phone} onChange={set('phone')} placeholder="+593 99 000 0000" /></Field>
        <Field label="Empresa"><input className={inputCls} value={c.company} onChange={set('company')} /></Field>
        <Field label="Fuente">
          <input className={inputCls} value={c.source} onChange={set('source')} list="crm-sources" placeholder="Web, referido, feria…" />
          <datalist id="crm-sources">{meta.sources.map((s) => <option key={s} value={s} />)}</datalist>
        </Field>
        <Field label="Ciudad"><input className={inputCls} value={c.city} onChange={set('city')} /></Field>
        <Field label="País"><input className={inputCls} value={c.country} onChange={set('country')} /></Field>
        {meta.scope === 'own' ? <OwnOwner meta={meta} /> : (   // v1.12.1 · el backend lo deja a su nombre igual
        <Field label="Responsable">
          <select className={inputCls} value={c.owner_id} onChange={set('owner_id')}>
            <option value="">Sin asignar</option>
            {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        )}
        <Field label="Etiquetas" group>
          <div className="flex gap-2">
            <input className={inputCls} value={tagInput} onChange={(e) => setTagInput(e.target.value)} list="crm-tags"
                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Escriba y presione Enter" />
            <datalist id="crm-tags">{meta.tags.map((t) => <option key={t} value={t} />)}</datalist>
            <button type="button" className={btn.ghost} onClick={addTag}>Agregar</button>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1">{c.tags.map((t) => <Tag key={t} onRemove={() => setC({ ...c, tags: c.tags.filter((x) => x !== t) })}>{t}</Tag>)}</div>
        </Field>
      </div>
      {meta.fields.length > 0 && (
        <div className="mt-4 border-t pt-3">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Campos personalizados</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {meta.fields.map((f) => (
              <Field key={f.id} label={`${f.name}${f.required ? ' *' : ''}`} group={f.type === 'multiselect'}>
                <CustomInput field={f} value={c.custom[f.id]} onChange={(v) => setC({ ...c, custom: { ...c.custom, [f.id]: v } })} />
              </Field>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function ImportModal({ meta, onClose, onDone }) {
  const [csv, setCsv] = useState('');
  const [fname, setFname] = useState('');
  const [tags, setTags] = useState('');
  const [source, setSource] = useState('');
  const [onDup, setOnDup] = useState('skip');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = useMemo(() => csv.split(/\r?\n/).filter(Boolean).slice(0, 4), [csv]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) { toast.error('El archivo supera 5 MB.'); return; }
    setFname(f.name);
    const r = new FileReader();
    r.onload = () => setCsv(String(r.result || ''));
    r.readAsText(f, 'utf-8');
  };
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post('/contacts/import', { csv, tags: tags.split(',').map((t) => t.trim()).filter(Boolean), source, on_duplicate: onDup });
      setRes(r);
      toast.success(`Importación lista: ${r.created} nuevos, ${r.updated} actualizados`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Importar contactos" onClose={onClose} wide
           footer={res ? <button className={btn.primary} onClick={onClose}>Cerrar</button>
             : <><button className={btn.ghost} onClick={onClose}>Cancelar</button><button className={btn.primary} disabled={!csv || busy} onClick={run}>{busy ? 'Importando…' : 'Importar'}</button></>}>
      {res ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[['Nuevos', res.created], ['Actualizados', res.updated], ['Omitidos', res.skipped]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-gray-50 py-3"><div className="text-xl font-extrabold">{v}</div><div className="text-xs text-gray-500">{l}</div></div>
            ))}
          </div>
          <p className="text-gray-600">Columnas reconocidas: {res.columns.join(', ') || 'ninguna'}</p>
          {res.errors.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {res.errors.map((er, i) => <div key={i}>{er}</div>)}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Archivo CSV separado por coma, punto y coma o tabulación. La primera fila son los encabezados: Nombre, Apellido, Correo, Teléfono,
            Empresa, Fuente, Ciudad, País, Etiquetas (separadas por coma) y el nombre de cualquier campo personalizado. Máximo 5.000 filas.
          </p>
          <label className={`${btn.ghost} cursor-pointer`}>
            <Upload size={15} /> {fname || 'Elegir archivo CSV'}
            <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={pick} />
          </label>
          {preview.length > 0 && (
            <pre className="max-h-28 overflow-auto rounded-lg bg-gray-50 p-2 text-[11px] text-gray-700">{preview.join('\n')}</pre>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Agregar etiquetas" hint="Separadas por coma"><input className={inputCls} value={tags} onChange={(e) => setTags(e.target.value)} /></Field>
            <Field label="Fuente por defecto">
              <input className={inputCls} value={source} onChange={(e) => setSource(e.target.value)} list="crm-sources-imp" />
              <datalist id="crm-sources-imp">{meta.sources.map((s) => <option key={s} value={s} />)}</datalist>
            </Field>
            <Field label="Si el contacto ya existe">
              <select className={inputCls} value={onDup} onChange={(e) => setOnDup(e.target.value)}>
                <option value="skip">Omitirlo</option><option value="update">Actualizarlo</option>
              </select>
            </Field>
          </div>
        </div>
      )}
    </Modal>
  );
}

function BulkModal({ kind, meta, count, onClose, onRun }) {
  const [tags, setTags] = useState('');
  const [owner, setOwner] = useState('');
  const titles = { add_tags: 'Agregar etiquetas', remove_tags: 'Quitar etiquetas', assign: 'Asignar responsable', delete: 'Eliminar contactos' };
  const go = () => onRun({ action: kind, tags: tags.split(',').map((t) => t.trim()).filter(Boolean), owner_id: owner || null });
  return (
    <Modal title={`${titles[kind]} · ${count} seleccionados`} onClose={onClose}
           footer={<><button className={btn.ghost} onClick={onClose}>Cancelar</button>
             <button className={kind === 'delete' ? btn.danger : btn.primary} onClick={go}>{kind === 'delete' ? 'Eliminar' : 'Aplicar'}</button></>}>
      {kind === 'delete' && <p className="text-sm text-gray-700">Se eliminarán {count} contactos con sus notas, tareas y actividad. Sus oportunidades se conservan, sin contacto. No se puede deshacer.</p>}
      {(kind === 'add_tags' || kind === 'remove_tags') && (
        <Field label="Etiquetas" hint="Separadas por coma">
          <input className={inputCls} value={tags} onChange={(e) => setTags(e.target.value)} list="crm-tags-bulk" autoFocus />
          <datalist id="crm-tags-bulk">{meta.tags.map((t) => <option key={t} value={t} />)}</datalist>
        </Field>
      )}
      {kind === 'assign' && (
        <Field label="Responsable">
          <select className={inputCls} value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Sin asignar</option>
            {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
      )}
    </Modal>
  );
}

export default function CrmContacts({ meta, reloadMeta }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [qInput, setQInput] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [], page_size: 25 });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [modal, setModal] = useState(null);
  const [activeList, setActiveList] = useState('');
  const users = useMemo(() => Object.fromEntries(meta.users.map((u) => [u.id, u.name])), [meta.users]);

  useEffect(() => { const t = setTimeout(() => { setFilters((f) => ({ ...f, q: qInput })); setPage(1); }, 300); return () => clearTimeout(t); }, [qInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get('/contacts', { ...toParams(filters), page, page_size: 25 })); setSel([]); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); setActiveList(''); };
  const applyList = (l) => {
    setActiveList(l?.id || '');
    const f = { ...EMPTY_FILTERS, ...(l?.filters || {}) };
    setFilters(f); setQInput(f.q || ''); setPage(1);
  };
  const saveList = async () => {
    const name = window.prompt('Nombre de la lista guardada');
    if (!name) return;
    try { const l = await api.post('/smart-lists', { name, filters: toParams(filters) }); toast.success('Lista guardada'); await reloadMeta(); setActiveList(l.id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const deleteList = async (id) => {
    try { await api.del(`/smart-lists/${id}`); await reloadMeta(); if (activeList === id) applyList(null); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportCsv = async () => {
    try { downloadBlob(await api.blob('/contacts/export', toParams(filters)), `contactos_${new Date().toISOString().slice(0, 10)}.csv`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runBulk = async (body) => {
    try {
      const r = await api.post('/contacts/bulk', { ...body, ids: sel });
      toast.success(`${r.affected} contactos actualizados`);
      setModal(null); load(); reloadMeta();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const allOnPage = data.items.length > 0 && data.items.every((c) => sel.includes(c.id));
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const activeCount = ['tags', 'source', 'owner_id', 'created_from', 'created_to', 'has_email', 'has_phone'].filter((k) => filters[k] !== '').length;

  return (
    <div className="space-y-3" data-testid="crm-contacts">
      <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={() => applyList(null)} className={`rounded-full px-3 py-1 text-xs font-semibold ${!activeList ? 'bg-[#2F3D8E] text-white' : 'bg-white text-gray-700 border border-gray-300'}`}>Todos</button>
        {meta.smart_lists.map((l) => (
          <span key={l.id} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${activeList === l.id ? 'bg-[#2F3D8E] text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>
            <button onClick={() => applyList(l)}>{l.name}</button>
            <button onClick={() => deleteList(l.id)} aria-label={`Eliminar lista ${l.name}`} className="opacity-60 hover:opacity-100"><X size={11} /></button>
          </span>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className={`${inputCls} pl-9`} data-testid="contacts-search" placeholder="Buscar por nombre, correo, teléfono o empresa" value={qInput} onChange={(e) => setQInput(e.target.value)} />
        </div>
        <select className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" value={filters.sort} onChange={(e) => setF('sort', e.target.value)} aria-label="Orden">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button className={btn.ghost} onClick={() => setShowFilters((s) => !s)}><Filter size={15} /> Filtros{activeCount ? ` (${activeCount})` : ''}</button>
        <button className={btn.ghost} onClick={saveList} title="Guardar estos filtros como lista"><Bookmark size={15} /> Guardar vista</button>
        <button className={btn.ghost} onClick={() => setModal({ t: 'import' })} data-testid="contacts-import"><Upload size={15} /> Importar</button>
        <button className={btn.ghost} onClick={exportCsv} data-testid="contacts-export"><Download size={15} /> Exportar</button>
        <button className={btn.primary} onClick={() => setModal({ t: 'new' })} data-testid="contacts-new"><Plus size={15} /> Contacto</button>
      </div>

      {showFilters && (
        <div className="grid gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Etiqueta">
            <select className={inputCls} value={filters.tags} onChange={(e) => setF('tags', e.target.value)}>
              <option value="">Cualquiera</option>{meta.tags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Fuente">
            <select className={inputCls} value={filters.source} onChange={(e) => setF('source', e.target.value)}>
              <option value="">Cualquiera</option>{meta.sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Responsable">
            <select className={inputCls} value={filters.owner_id} onChange={(e) => setF('owner_id', e.target.value)}>
              <option value="">Cualquiera</option><option value="unassigned">Sin asignar</option>
              {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Correo">
              <select className={inputCls} value={filters.has_email} onChange={(e) => setF('has_email', e.target.value)}>
                <option value="">—</option><option value="true">Con correo</option><option value="false">Sin correo</option>
              </select>
            </Field>
            <Field label="Teléfono">
              <select className={inputCls} value={filters.has_phone} onChange={(e) => setF('has_phone', e.target.value)}>
                <option value="">—</option><option value="true">Con teléfono</option><option value="false">Sin teléfono</option>
              </select>
            </Field>
          </div>
          <Field label="Creado desde"><input type="date" className={inputCls} value={filters.created_from} onChange={(e) => setF('created_from', e.target.value)} /></Field>
          <Field label="Creado hasta"><input type="date" className={inputCls} value={filters.created_to} onChange={(e) => setF('created_to', e.target.value)} /></Field>
          <div className="flex items-end"><button className={btn.ghost} onClick={() => { setFilters({ ...EMPTY_FILTERS, q: filters.q }); setActiveList(''); }}>Limpiar filtros</button></div>
        </div>
      )}

      {sel.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[#2F3D8E]/5 px-3 py-2 text-sm">
          <b>{sel.length} seleccionados</b>
          <button className={btn.ghost} onClick={() => setModal({ t: 'bulk', kind: 'add_tags' })}><TagIcon size={14} /> Agregar etiquetas</button>
          <button className={btn.ghost} onClick={() => setModal({ t: 'bulk', kind: 'remove_tags' })}><TagIcon size={14} /> Quitar etiquetas</button>
          {meta.scope !== 'own' && <button className={btn.ghost} onClick={() => setModal({ t: 'bulk', kind: 'assign' })}><UserCheck size={14} /> Asignar</button>}
          {meta.is_admin && <button className={btn.danger} onClick={() => setModal({ t: 'bulk', kind: 'delete' })}><Trash2 size={14} /> Eliminar</button>}
        </div>
      )}

      {!loading && data.total === 0 ? (
        <Empty title={filters.q || activeCount ? 'Ningún contacto coincide' : 'Aún no hay contactos'}
               action={<button className={btn.primary} onClick={() => setModal({ t: 'new' })}><Plus size={15} /> Crear contacto</button>}>
          {filters.q || activeCount ? 'Pruebe con otra búsqueda o quite filtros.' : 'Cree el primero o importe un CSV con su base actual.'}
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="w-10 px-3 py-2.5"><input type="checkbox" aria-label="Seleccionar página" checked={allOnPage}
                  onChange={() => setSel(allOnPage ? sel.filter((id) => !data.items.some((c) => c.id === id)) : [...new Set([...sel, ...data.items.map((c) => c.id)])])} /></th>
                {['Nombre', 'Teléfono', 'Correo', 'Empresa', 'Responsable', 'Creado', 'Última actividad', 'Etiquetas'].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody className={loading ? 'opacity-50' : ''}>
              {data.items.map((c) => (
                <tr key={c.id} className="cursor-pointer border-t hover:bg-gray-50" data-testid="contact-row" onClick={() => navigate(`/crm/contactos/${c.id}`)}>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Seleccionar ${c.name}`} checked={sel.includes(c.id)}
                           onChange={() => setSel(sel.includes(c.id) ? sel.filter((x) => x !== c.id) : [...sel, c.id])} />
                  </td>
                  <td className="px-3 py-2 font-semibold text-[#0F2A3A]">{c.name} <LeadBadge level={c.ai_level} score={c.ai_score} /></td>
                  <td className="px-3 py-2 text-gray-700">{c.phone || '—'}</td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-gray-700">{c.email || '—'}</td>
                  <td className="max-w-[160px] truncate px-3 py-2 text-gray-700">{c.company || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{users[c.owner_id] || '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-500">{fdate(c.created_at)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-500">{fdatetime(c.last_activity_at)}</td>
                  <td className="px-3 py-2"><div className="flex max-w-[220px] flex-wrap gap-1">{(c.tags || []).slice(0, 3).map((t) => <Tag key={t}>{t}</Tag>)}{c.tags?.length > 3 && <span className="text-[11px] text-gray-500">+{c.tags.length - 3}</span>}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-gray-600">
            <span>{data.total} contactos</span>
            <div className="flex items-center gap-2">
              <button className={btn.ghost} disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button>
              <span>Página {page} de {pages}</span>
              <button className={btn.ghost} disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      )}

      {modal?.t === 'new' && <ContactForm meta={meta} onClose={() => setModal(null)} onSaved={(c) => { setModal(null); reloadMeta(); navigate(`/crm/contactos/${c.id}`); }} />}
      {modal?.t === 'import' && <ImportModal meta={meta} onClose={() => setModal(null)} onDone={() => { load(); reloadMeta(); }} />}
      {modal?.t === 'bulk' && <BulkModal kind={modal.kind} meta={meta} count={sel.length} onClose={() => setModal(null)} onRun={runBulk} />}
    </div>
  );
}
